import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

export interface UserProfile {
  user_id: number;
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  avatarUrl?: string;
  phone_number?: string;
  language?: string;
  country?: string;
  state?: string;
  city?: string;
  address?: string;
  postal_code?: string;
  completion_percentage?: number;
  role_id: number;
  role_name: string;
  available_assessments: number;
}

export interface SearchUserResponse {
  success: boolean;
  message: string;
  page: number;
  limit: number;
  total: number;
  data: Array<{
    user_id: number;
    first_name: string;
    last_name: string;
    email: string;
    avatarUrl?: string;
    created_at: string;
    role: {
      role_id: string;
      role_name: string;
    };
    practitioner_name?: string;
    available_assessments: number;
  }>;
}

export interface SearchUserParams {
  name: string;
  page?: number;
  limit?: number;
  role_name?: string; // filtro opcional por role
}

export interface Organization {
  org_id: number;
  org_name: string;
}

export interface Group {
  group_id: number;
  group_name: string;
  total_members: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/users`;

  constructor(private http: HttpClient) {}

  // GET: Buscar usuários paginados com filtro opcional por role_name
  getPaginated(page: number = 1, limit: number = 10, roleName?: string): Observable<any> {
    const params: any = { page, limit };
    if (roleName && roleName.trim() !== '') {
      params.role_name = roleName;
    }
    // Novo formato: /users?page=1&limit=12[&role_name=...]
    return this.http.get(this.apiUrl, { params });
  }

  // GET: Detalhes do usuário por ID (com perfil + org + grupos)
  getUserById(id: number): Observable<{
    success: boolean;
    message: string;
    data: UserProfile;
    organization: Organization | null;
    groups: Group[];
  }> {
    return this.http.get<{
      success: boolean;
      message: string;
      data: UserProfile;
      organization: Organization | null;
      groups: Group[];
    }>(`${this.apiUrl}/${id}`);
  }

  // GET: Buscar usuários por nome com paginação
  searchUsersByName(params: SearchUserParams): Observable<SearchUserResponse> {
    // Validação: mínimo de 2 caracteres
    if (!params.name || params.name.length < 2) {
      throw new Error('Nome deve ter pelo menos 2 caracteres');
    }

    const page = params.page || 1;
    const limit = params.limit || 12;
    
    // Ajuste para o novo formato baseado em query params
    const queryParams: any = { name: params.name, page, limit };
    if (params.role_name && params.role_name.trim() !== '') {
      queryParams.role_name = params.role_name;
    }
    return this.http.get<SearchUserResponse>(`${environment.apiUrl}/users/search`, { params: queryParams });
  }

  // Método auxiliar para busca simples (sem paginação)
  searchUsersSimple(name: string): Observable<SearchUserResponse> {
    return this.searchUsersByName({ name });
  }

  // POST: Criar novo usuário
  create(userData: any): Observable<any> {
    return this.http.post(this.apiUrl, userData);
  }

  // PUT: Atualizar usuário
  update(id: number, userData: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, userData);
  }

  // PUT: Atualiza a role do usuário
  updateRole(userId: number, roleData: { role_id: number }): Observable<any> {
    console.log('Enviando promoção:', roleData);
    return this.http.put(`${this.apiUrl}/${userId}/promote`, roleData);
  }

  // DELETE: Deletar usuário
  delete(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  sendInvite(data: { name: string; email: string; group_id?: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}/invite`, data);
  }

  sendPasswordReset(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  // PUT: Atualiza o idioma do usuário autenticado no backend
  updateLanguage(languageId: number): Observable<any> {
    return this.http.put(`${this.apiUrl}/update-language`, {
      language_id: languageId,
    });
  }

  // GET: Avaliações de um usuário específico
  getAssessmentsByUserId(userId: number): Observable<{
    status: string;
    user_id: number;
    assessments: any[];
  }> {
    return this.http.get<{
      status: string;
      user_id: number;
      assessments: any[];
    }>(`${this.apiUrl}/assessments/${userId}`);
  }

  // GET: Lista todos os usuários com role Practitioner ou Master Practitioner
  // Endpoints válidos: /users/practitioners/1/12 ou /users/practitioners?page=1&limit=12
  getPractitionersAndMasters(page: number = 1, limit: number = 12, search: string = ''): Observable<{
    success: boolean;
    message: string;
    page: number;
    limit: number;
    total: number;
    data: Array<{
      user_id: string;
      first_name: string;
      last_name: string;
      email: string;
      avatarUrl?: string;
      role: {
        role_id: string;
        role_name: string;
      };
    }>;
  }> {
    let url = `${this.apiUrl}/practitioners/${page}/${limit}`;
    if (search) {
      url += `?search=${encodeURIComponent(search)}`;
    }
    return this.http.get<{
      success: boolean;
      message: string;
      page: number;
      limit: number;
      total: number;
      data: Array<{
        user_id: string;
        first_name: string;
        last_name: string;
        email: string;
        avatarUrl?: string;
        role: {
          role_id: string;
          role_name: string;
        };
      }>;
    }>(url);
  }

  // POST: Atribuir um practitioner a um usuário
  assignPractitionerToUser(userId: number, practitionerId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/assign-practitioner/${userId}`, {
      practitioner_id: practitionerId,
    });
  }

  // GET: Indicadores de usuários
  getUserIndicators(): Observable<any> {
    return this.http.get(`${this.apiUrl}/indicators`);
  }

  // GET: Listar convites enviados pelo usuário logado
  getInvitations(page: number = 1, limit: number = 12): Observable<any> {
    return this.http.get(`${this.apiUrl}/invitations`, { params: { page, limit } });
  }

  // POST: Reenviar convite
  resendInvitation(invitationId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/invitations/${invitationId}/resend`, {});
  }

  // DELETE: Excluir convite
  deleteInvitation(invitationId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/invitations/${invitationId}`);
  }
}
